import api from "./api";

const BASE_URL = "/calls";

const callService = {
  // Twilio access token
  getToken: async () => {
    return api.get(`${BASE_URL}/token`);
  },

  // Start call (register record + lock lead)
  startCall: async ({ toNumber, leadId, patientId, locationId, patientName, pipelineId, stageId, stageName, callerId, metadata = {} }) => {
    return api.post(`${BASE_URL}/start`, { toNumber, leadId, patientId, locationId, patientName, pipelineId, stageId, stageName, callerId, metadata });
  },

  // Register Twilio call SID after Device connects
  registerCallSid: async ({ twilioCallSid, leadId, patientId, locationId, pipelineId, stageId, stageName, dialerSessionId }) => {
    return api.post(`${BASE_URL}/register-sid`, { twilioCallSid, leadId, patientId, locationId, pipelineId, stageId, stageName, dialerSessionId });
  },

  cancelCall: async (twilioCallSid) => {
    return api.post(`${BASE_URL}/cancel`, { twilioCallSid });
  },

  getCallStatus: async (twilioCallSid) => {
    return api.get(`${BASE_URL}/status/${twilioCallSid}`);
  },

  getCallStatusCached: async (callSid) => {
    return api.get(`${BASE_URL}/call-status-cached/${callSid}?_t=${Date.now()}`);
  },

  dropVoicemail: async (twilioCallSid, voicemailUrl) => {
    return api.post(`${BASE_URL}/drop-voicemail`, { twilioCallSid, voicemailUrl });
  },

  getActiveCalls: async () => {
    return api.get(`${BASE_URL}/active`);
  },

  checkCanCall: async () => {
    return api.get(`${BASE_URL}/can-call`);
  },

  getCallStats: async ({ locationId, startDate, endDate } = {}) => {
    const params = new URLSearchParams();
    if (locationId) params.append("locationId", locationId);
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    return api.get(`${BASE_URL}/stats?${params}`);
  },

  getLockedLeads: async (locationId) => {
    const params = new URLSearchParams();
    if (locationId) params.append("locationId", locationId);
    return api.get(`${BASE_URL}/locked-leads?${params}`);
  },

  // Call history
  getCallHistory: async ({ locationId, direction, dateFrom, dateTo, status, outcome, search, page, limit } = {}) => {
    const params = new URLSearchParams();
    if (locationId) params.append("locationId", locationId);
    if (direction) params.append("direction", direction);
    if (dateFrom) params.append("dateFrom", dateFrom);
    if (dateTo) params.append("dateTo", dateTo);
    if (status) params.append("status", status);
    if (outcome) params.append("outcome", outcome);
    if (search) params.append("search", search);
    if (page) params.append("page", page);
    if (limit) params.append("limit", limit);
    return api.get(`${BASE_URL}/history?${params}`);
  },

  getContactCallHistory: async (patientId) => {
    return api.get(`${BASE_URL}/contact/${patientId}/history`);
  },

  updateCallOutcome: async (twilioCallSid, payload) => {
    return api.patch(`${BASE_URL}/history/${twilioCallSid}/outcome`, payload);
  },

  getCallLogs: async (params = {}) => {
    const search = new URLSearchParams(params);
    return api.get(`${BASE_URL}/logs?${search}`);
  },

  // Power Dialer
  createDialerSession: async ({ leads, voicemailUrl, callerId, locationId }) => {
    return api.post(`${BASE_URL}/dialer/session`, { leads, voicemailUrl, callerId, locationId });
  },

  getActiveDialerSession: async () => {
    return api.get(`${BASE_URL}/dialer/sessions/active`);
  },

  getDialerSession: async (sessionId) => {
    return api.get(`${BASE_URL}/dialer/session/${sessionId}`);
  },

  dialCurrentLead: async (sessionId) => {
    return api.post(`${BASE_URL}/dialer/${sessionId}/dial`);
  },

  moveToNextLead: async (sessionId, expectedIndex = null) => {
    return api.post(`${BASE_URL}/dialer/${sessionId}/next`, {
      ...(Number.isInteger(expectedIndex) ? { expectedIndex } : {}),
    });
  },

  skipCurrentLead: async (sessionId) => {
    return api.post(`${BASE_URL}/dialer/${sessionId}/skip`);
  },

  markNoAnswer: async (sessionId) => {
    return api.post(`${BASE_URL}/dialer/${sessionId}/no-answer`);
  },

  updateDialerLeadOutcome: async (sessionId, { leadIndex, status, outcome, notes, callSid, duration }) => {
    return api.patch(`${BASE_URL}/dialer/${sessionId}/lead-outcome`, { leadIndex, status, outcome, notes, callSid, duration });
  },

  pauseDialerSession: async (sessionId) => {
    return api.post(`${BASE_URL}/dialer/${sessionId}/pause`);
  },

  resumeDialerSession: async (sessionId) => {
    return api.post(`${BASE_URL}/dialer/${sessionId}/resume`);
  },

  stopDialerSession: async (sessionId) => {
    return api.post(`${BASE_URL}/dialer/${sessionId}/stop`);
  },

  completeWrapUp: async () => {
    return api.post(`/agent-status/complete-wrap-up`);
  },
};

export default callService;
